import { normaliseFieldValue, normaliseOptions, normaliseType } from './optionUtils.js';
import { getDomainNumericMaximum, isAssessmentDomain } from './recordingContract.js';

const ENGINE_FIELDS = new Set(['userId', 'timestamp']);

export function isActiveDomainInput(item) {
  return Boolean(item) && item.active !== false && item.retired !== true;
}

export function domainInputFields(domain) {
  return (domain?.primary
    ? [domain.primary, ...(domain.followUp || [])]
    : (domain?.followUp || domain?.fields || [])).filter(isActiveDomainInput);
}

export function domainInputOptions(field, { legacy = false } = {}) {
  return normaliseOptions((field?.options || []).filter(isActiveDomainInput).map(option => {
    if (legacy && typeof option === 'string') return { value: option, label: option };
    return option?.value == null && option?.key != null ? { ...option, value: option.key } : option;
  }));
}

export function isMissingDomainInput(value) {
  return value == null || (typeof value === 'string' && !value.trim());
}

export function parseDomainInputNumber(raw) {
  if (isMissingDomainInput(raw) || !['number', 'string'].includes(typeof raw)) return null;
  const value = Number(typeof raw === 'string' ? raw.replace(',', '.') : raw);
  return Number.isFinite(value) ? value : null;
}

/** Hidden/retired parents cannot leave their dependent answers active. */
export function isDomainInputVisible(field, values, fields, ancestors = new Set()) {
  if (!isActiveDomainInput(field) || ancestors.has(field.id)) return false;
  const nextAncestors = new Set([...ancestors, field.id]);
  return Object.entries(field.showWhen || {}).every(([id, expected]) => {
    const parent = fields?.find(item => item.id === id);
    return (!fields || (parent && isDomainInputVisible(parent, values, fields, nextAncestors)))
      && (values?.[id] ?? null) === expected;
  });
}

/** Native controlled number inputs retain text while typing; stored values don't. */
export function hasCompleteDomainInputs(domain, values = {}, { maximum, coerceNumbers = false, includePrimary = true } = {}) {
  const fields = domainInputFields(domain);
  if (domain?.primary && !isActiveDomainInput(domain.primary)) return false;
  if (!fields.some(field => !ENGINE_FIELDS.has(field.type))) return !includePrimary;
  if (includePrimary && domain?.primary?.type === 'number' && maximum != null
    && !(parseDomainInputNumber(maximum) > 0)) return false;

  return fields.every(field => {
    const primary = field === domain?.primary;
    if (primary && !includePrimary) return true;
    if (ENGINE_FIELDS.has(field.type)) return !primary;
    if (!primary && !isDomainInputVisible(field, values, fields)) return true;
    const raw = values[field.id];
    if (isMissingDomainInput(raw)) return !primary && field.optional !== false;
    const type = normaliseType(field.type);
    if (type === 'boolean') {
      // Older fields[] list schemas explicitly offered Absent alongside No.
      return typeof raw === 'boolean'
        || (!domain?.primary && field.type === 'binary' && raw === 'absent');
    }
    if (['number', 'rating', 'star'].includes(type)) {
      const value = coerceNumbers ? parseDomainInputNumber(raw) : raw;
      if (typeof value !== 'number' || !Number.isFinite(value)) return false;
      const rating = type === 'rating' || type === 'star';
      const limit = primary && type === 'number'
        ? getDomainNumericMaximum(domain, maximum) : Number(field.maxScore);
      if (rating && (value < 1 || !Number.isInteger(value))) return false;
      const effectiveLimit = limit > 0 ? limit : rating ? 5 : null;
      return (effectiveLimit == null || value <= effectiveLimit)
        && (!primary || !isAssessmentDomain(domain) || value >= 0);
    }
    if (type === 'multiChoice') {
      return domainInputOptions(field, { legacy: !domain?.primary })
        .some(option => String(option.value) === String(raw));
    }
    if (['string', 'text', 'storageRef'].includes(type)) return typeof raw === 'string';
    return false;
  });
}

/** Only configured, active, visible answers become new event fields. */
export function projectDomainInputValues(domain, values = {}, { includePrimary = true } = {}) {
  const fields = domainInputFields(domain);
  return Object.fromEntries(fields
    .filter(field => (includePrimary || field !== domain?.primary)
      && !ENGINE_FIELDS.has(field.type) && isDomainInputVisible(field, values, fields)
      && Object.hasOwn(values, field.id) && !isMissingDomainInput(values[field.id]))
    .map(field => {
      const raw = values[field.id];
      const value = ['number', 'rating', 'star'].includes(field.type)
        ? parseDomainInputNumber(raw)
        : !domain?.primary && field.type === 'binary' && raw === 'absent'
          ? raw : normaliseFieldValue(field.type === 'multiChoice'
            ? { ...field, options: domainInputOptions(field, { legacy: !domain?.primary }) } : field, raw);
      return [field.id, value];
    }));
}
